import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import RequireAuth from '../../components/guards/RequireAuth';
import { useAuth } from '../../components/AuthProvider';
import { apiUrl } from '../../lib/api';
import { useUpgradeModal } from '../../contexts/UpgradeModalContext';

const MONO = "'JetBrains Mono', monospace";
const ORBITRON = "'Orbitron', monospace";
const TEAL = '#00FFE5';
const TEAL_DIM = 'rgba(0,255,229,0.6)';
const TEAL_FAINT = 'rgba(0,255,229,0.08)';
const TEAL_BORDER = 'rgba(0,255,229,0.2)';
const BG = '#080810';
const CARD_BG = 'rgba(13,13,26,0.95)';
const MUTED = 'rgba(0,255,229,0.45)';
const WHITE = '#E8E8F0';
const GREEN = '#2DC896';
const ORANGE = '#FF9500';
const RED = '#FF4444';
const PURPLE = '#7B2FFF';

function StagePill({ stage }) {
  const colors = {
    prospect: { bg: 'rgba(123,47,255,0.15)', color: '#7B2FFF', border: 'rgba(123,47,255,0.3)' },
    qualified: { bg: 'rgba(0,255,229,0.1)', color: TEAL, border: TEAL_BORDER },
    proposal: { bg: 'rgba(255,149,0,0.12)', color: ORANGE, border: 'rgba(255,149,0,0.3)' },
    negotiation: { bg: 'rgba(255,68,68,0.12)', color: RED, border: 'rgba(255,68,68,0.3)' },
    closed_won: { bg: 'rgba(45,200,150,0.12)', color: GREEN, border: 'rgba(45,200,150,0.3)' },
    closed_lost: { bg: 'rgba(100,100,120,0.15)', color: '#666688', border: 'rgba(100,100,120,0.3)' },
    active: { bg: 'rgba(45,200,150,0.12)', color: GREEN, border: 'rgba(45,200,150,0.3)' },
    onboarding: { bg: 'rgba(0,255,229,0.1)', color: TEAL, border: TEAL_BORDER },
    delivery: { bg: 'rgba(255,149,0,0.12)', color: ORANGE, border: 'rgba(255,149,0,0.3)' },
    at_risk: { bg: 'rgba(255,68,68,0.12)', color: RED, border: 'rgba(255,68,68,0.3)' },
  };
  const key = (stage || '')?.toLowerCase()?.replace(/\s+/g, '_');
  const c = colors?.[key] || { bg: TEAL_FAINT, color: TEAL_DIM, border: TEAL_BORDER };
  return (
    <span style={{ fontFamily: MONO, fontSize: '0.6rem', letterSpacing: '0.1em', padding: '2px 8px', borderRadius: 3, background: c?.bg, color: c?.color, border: `1px solid ${c?.border}`, textTransform: 'uppercase' }}>
      {stage || 'UNKNOWN'}
    </span>
  );
}

function IntentCircle({ score }) {
  const s = parseInt(score) || 0;
  const color = s >= 75 ? RED : s >= 50 ? ORANGE : TEAL_DIM;
  return (
    <div style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: '0.55rem', color, flexShrink: 0 }}>
      {s}
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '16px 20px', flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: ORBITRON, fontSize: '1.4rem', fontWeight: 700, color: color || TEAL }}>{value ?? '—'}</div>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
      <div style={{ width: 3, height: 20, background: TEAL, borderRadius: 2 }} />
      <span style={{ fontFamily: ORBITRON, fontSize: '0.85rem', fontWeight: 700, color: TEAL, letterSpacing: '0.15em' }}>{title}</span>
    </div>
  );
}

function SimpleMarkdown({ content }) {
  if (!content) return <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED }}>No content available.</span>;

  // Strip fenced code block markers (``` lines) and render content as plain text
  const stripped = content?.replace(/^```[^\n]*\n?/gm, '')?.replace(/^```\s*$/gm, '');

  const lines = stripped?.split('\n');

  // Render inline formatting: ++bold++, **bold**, *italic*, `code` (plain)
  function renderInline(text) {
    const parts = [];
    // Combined regex for all inline patterns
    const regex = /\+\+(.+?)\+\+|\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`/g;
    let last = 0;
    let match;
    let idx = 0;
    while ((match = regex?.exec(text)) !== null) {
      if (match?.index > last) parts?.push(<span key={idx++}>{text?.slice(last, match?.index)}</span>);
      if (match?.[1] !== undefined) parts?.push(<strong key={idx++} style={{ fontWeight: 700, color: 'rgba(232,232,240,1)' }}>{match?.[1]}</strong>);
      else if (match?.[2] !== undefined) parts?.push(<strong key={idx++} style={{ fontWeight: 700, color: 'rgba(232,232,240,1)' }}>{match?.[2]}</strong>);
      else if (match?.[3] !== undefined) parts?.push(<em key={idx++} style={{ fontStyle: 'italic' }}>{match?.[3]}</em>);
      else if (match?.[4] !== undefined) parts?.push(<span key={idx++}>{match?.[4]}</span>);
      last = match?.index + match?.[0]?.length;
    }
    if (last < text?.length) parts?.push(<span key={idx++}>{text?.slice(last)}</span>);
    return parts?.length > 0 ? parts : text;
  }

  // Detect table block: consecutive lines starting with |
  const rendered = [];
  let i = 0;
  while (i < lines?.length) {
    const line = lines?.[i];
    // Table detection
    if (line?.trim()?.startsWith('|') && line?.trim()?.endsWith('|')) {
      const tableLines = [];
      while (i < lines?.length && lines?.[i]?.trim()?.startsWith('|') && lines?.[i]?.trim()?.endsWith('|')) {
        tableLines?.push(lines?.[i]);
        i++;
      }
      // Filter out separator rows (e.g. |---|---|)
      const dataRows = tableLines?.filter(r => !/^\s*\|[\s\-|:]+\|\s*$/?.test(r));
      rendered?.push(
        <table key={`tbl-${i}`} style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8, marginBottom: 8 }}>
          <tbody>
            {dataRows?.map((row, ri) => {
              const cells = row?.trim()?.replace(/^\||\|$/g, '')?.split('|');
              return (
                <tr key={ri} style={{ borderBottom: `1px solid rgba(0,255,229,0.1)` }}>
                  {cells?.map((cell, ci) => (
                    <td key={ci} style={{ padding: '4px 8px', fontFamily: MONO, fontSize: '0.7rem', color: ri === 0 ? TEAL : 'rgba(232,232,240,0.85)', fontWeight: ri === 0 ? 700 : 400 }}>
                      {renderInline(cell?.trim())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      );
      continue;
    }
    if (line?.startsWith('### ')) { rendered?.push(<div key={i} style={{ color: TEAL, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.1em', marginTop: 12, marginBottom: 4 }}>{renderInline(line?.slice(4))}</div>); }
    else if (line?.startsWith('## ')) { rendered?.push(<div key={i} style={{ color: WHITE, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', marginTop: 14, marginBottom: 6 }}>{renderInline(line?.slice(3))}</div>); }
    else if (line?.startsWith('# ')) { rendered?.push(<div key={i} style={{ color: WHITE, fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.08em', marginTop: 16, marginBottom: 8 }}>{renderInline(line?.slice(2))}</div>); }
    else if (line?.startsWith('- ') || line?.startsWith('* ')) { rendered?.push(<div key={i} style={{ paddingLeft: 12, marginBottom: 2 }}>· {renderInline(line?.slice(2))}</div>); }
    else if (line?.trim() === '') { rendered?.push(<div key={i} style={{ height: 6 }} />); }
    else { rendered?.push(<div key={i} style={{ marginBottom: 2 }}>{renderInline(line)}</div>); }
    i++;
  }

  return (
    <div style={{ fontFamily: MONO, fontSize: '0.72rem', color: 'rgba(232,232,240,0.85)', lineHeight: 1.7 }}>
      {rendered}
    </div>
  );
}

function OperationsPage() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { show: showUpgradeModal } = useUpgradeModal();
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesBrief, setSalesBrief] = useState(null);
  const [deliveryBrief, setDeliveryBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proofData, setProofData] = useState(null);
  const [proofLoading, setProofLoading] = useState(true);
  const [proofError, setProofError] = useState(null);

  // ─── CONTENT CREW STATE ───
  const [contentForm, setContentForm] = useState({
    goal: '',
    platform: [],
    audience: '',
    offer: '',
    tone: 'Professional',
    duration: '7 days',
    content_types: [],
  });
  const [contentRunning, setContentRunning] = useState(false);
  const [contentResults, setContentResults] = useState([]);
  const [contentError, setContentError] = useState(null);
  const [approvingIds, setApprovingIds] = useState({});
  const [approvedIds, setApprovedIds] = useState({});

  const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'LinkedIn', 'Email', 'Blog'];
  const CONTENT_TYPES = ['Posts', 'Scripts', 'Calendar', 'Emails', 'Blog', 'Ads', 'Repurpose'];
  const TONES = ['Professional', 'Casual', 'Bold', 'Educational'];
  const DURATIONS = ['7 days', '14 days', '30 days'];

  function toggleMulti(field, value) {
    setContentForm(prev => {
      const arr = prev?.[field];
      return { ...prev, [field]: arr?.includes(value) ? arr?.filter(v => v !== value) : [...arr, value] };
    });
  }

  async function executeContentMission() {
    setContentRunning(true);
    setContentError(null);
    setContentResults([]);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      const res = await fetch(apiUrl('/api/content-crew/run'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(contentForm),
      });
      if (!res?.ok) throw new Error(`HTTP ${res?.status}`);
      const json = await res?.json();
      const results = json?.results ?? json?.data ?? json?.outputs ?? (Array.isArray(json) ? json : []);
      setContentResults(results);
    } catch (err) {
      console.error('Content Crew run error:', err);
      setContentError(err?.message || 'Mission failed. Please try again.');
    } finally {
      setContentRunning(false);
    }
  }

  async function approveCard(card, idx) {
    const key = card?.id ?? idx;
    setApprovingIds(prev => ({ ...prev, [key]: true }));
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      if (!card?.id) throw new Error('Content piece is missing its persisted ID.');
      const res = await fetch(apiUrl('/api/content-crew/approve'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          content_piece_id: card.id,
          platform: contentForm.platform,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setApprovedIds(prev => ({ ...prev, [key]: true }));
    } catch (err) {
      console.error('Content Crew approve error:', err);
    } finally {
      setApprovingIds(prev => ({ ...prev, [key]: false }));
    }
  }

  const multiSelectStyle = (selected) => ({
    display: 'flex', flexWrap: 'wrap', gap: 6,
  });

  const chipStyle = (active) => ({
    fontFamily: MONO, fontSize: '0.62rem', letterSpacing: '0.08em',
    padding: '4px 10px', borderRadius: 3, cursor: 'pointer',
    border: `1px solid ${active ? TEAL : TEAL_BORDER}`,
    background: active ? 'rgba(0,255,229,0.12)' : TEAL_FAINT,
    color: active ? TEAL : MUTED,
    transition: 'all 0.15s',
    userSelect: 'none',
  });

  const inputStyle = {
    width: '100%', background: 'rgba(0,0,0,0.4)', border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 4, padding: '8px 12px', fontFamily: MONO, fontSize: '0.72rem',
    color: WHITE, outline: 'none', boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle, appearance: 'none', cursor: 'pointer',
  };

  const labelStyle = {
    fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em',
    marginBottom: 6, display: 'block',
  };

  // Teal spinner keyframes injected once
  const spinnerStyle = {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: `2px solid rgba(0,255,229,0.25)`,
    borderTopColor: TEAL,
    borderRadius: '50%',
    animation: 'contentCrewSpin 0.7s linear infinite',
    verticalAlign: 'middle',
    marginRight: 8,
    flexShrink: 0,
  };

  // ─── TEAM WORKSPACE STATE ───
  const [workspace, setWorkspace] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceError, setWorkspaceError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);
  const [removingIds, setRemovingIds] = useState({});

  console.log('[TeamWorkspace] userProfile.plan =', userProfile?.plan);
  const isElite = (userProfile?.plan?.toLowerCase()?.includes('elite')) || userProfile?.role === 'admin';

  async function fetchWorkspace() {
    setWorkspaceLoading(true);
    setWorkspaceError(null);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      const res = await fetch(apiUrl('/api/workspace'), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res?.status === 403) {
        const json = await res?.json()?.catch(() => ({}));
        showUpgradeModal(json?.required_plan || 'ELITE');
        setWorkspaceError('Access denied. Elite plan required.');
        return;
      }
      if (!res?.ok) throw new Error(`HTTP ${res?.status}`);
      const json = await res?.json();
      setWorkspace(json?.data ?? json);
    } catch (err) {
      setWorkspaceError(err?.message || 'Failed to load workspace.');
    } finally {
      setWorkspaceLoading(false);
    }
  }

  async function handleInvite(e) {
    e?.preventDefault();
    if (!inviteEmail?.trim()) return;
    setInviting(true);
    setInviteMsg(null);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      const res = await fetch(apiUrl('/api/workspace/invite'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ email: inviteEmail?.trim() }),
      });
      if (res?.status === 403) {
        const json = await res?.json()?.catch(() => ({}));
        showUpgradeModal(json?.required_plan || 'ELITE');
        return;
      }
      if (!res?.ok) {
        const json = await res?.json()?.catch(() => ({}));
        throw new Error(json?.message || json?.error || `HTTP ${res?.status}`);
      }
      setInviteMsg({ type: 'success', text: `Invite sent to ${inviteEmail?.trim()}` });
      setInviteEmail('');
      fetchWorkspace();
    } catch (err) {
      setInviteMsg({ type: 'error', text: err?.message || 'Invite failed.' });
    } finally {
      setInviting(false);
    }
  }

  async function handleRemoveMember(userId) {
    setRemovingIds(prev => ({ ...prev, [userId]: true }));
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const token = session?.access_token;
      const res = await fetch(apiUrl(`/api/workspace/member/${userId}`), {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res?.status === 403) {
        const json = await res?.json()?.catch(() => ({}));
        showUpgradeModal(json?.required_plan || 'ELITE');
        return;
      }
      if (!res?.ok) throw new Error(`HTTP ${res?.status}`);
      fetchWorkspace();
    } catch (err) {
      console.error('Remove member error:', err);
    } finally {
      setRemovingIds(prev => ({ ...prev, [userId]: false }));
    }
  }

  useEffect(() => {
    if (isElite) fetchWorkspace();
  }, [isElite]);

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      try {
        const [leadsRes, clientsRes, salesBriefRes, deliveryBriefRes] = await Promise.all([
          supabase?.from('leads')?.select('*')?.eq('user_id', user.id)?.order('created_at', { ascending: false }),
          supabase?.from('clients')?.select('*')?.eq('user_id', user.id)?.order('created_at', { ascending: false }),
          supabase?.from('command_briefs')?.select('*')?.eq('id', `sales_command_brief:${user.id}`)?.maybeSingle(),
          supabase?.from('command_briefs')?.select('*')?.eq('id', `delivery_command_brief:${user.id}`)?.maybeSingle(),
        ]);
        setLeads(leadsRes?.data || []);
        setClients(clientsRes?.data || []);
        setSalesBrief(salesBriefRes?.data || null);
        setDeliveryBrief(deliveryBriefRes?.data || null);
      } catch (err) {
        console.error('Operations fetch error:', err);
      } finally {
        setLoading(false);
      }
    }
    if (user?.id) fetchAll();
  }, [user?.id]);

  useEffect(() => {
    async function fetchProofSummary() {
      setProofLoading(true);
      try {
        const { data: sessionData } = await supabase?.auth?.getSession();
        const accessToken = sessionData?.session?.access_token;
        if (!accessToken) {
          setProofLoading(false);
          return;
        }
        const res = await fetch(apiUrl('/api/proof/summary'), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (!res?.ok) {
          console.error('Proof summary fetch failed:', res?.status, res?.statusText);
          setProofLoading(false);
          return;
        }
        const json = await res?.json();
        setProofData({
          hours_saved: json?.hours_saved ?? null,
          actions_executed: json?.actions_executed ?? null,
          revenue_influenced: json?.revenue_influenced ?? null,
          missions_completed: json?.missions_completed ?? null,
          leads_processed: json?.leads_processed ?? null,
          files_produced: json?.files_produced ?? null,
        });
      } catch (err) {
        console.error('Proof summary error:', err);
      } finally {
        setProofLoading(false);
      }
    }
    fetchProofSummary();
  }, []);

  // SALES CREW metrics
  const pipelineValue = leads?.reduce((sum, l) => sum + (parseFloat(l?.deal_value) || 0), 0);
  const hotLeads = leads?.filter(l => (parseInt(l?.intent_score) || 0) >= 75)?.length;
  const pendingApprovals = leads?.filter(l => l?.next_action_status === 'pending_approval')?.length;
  const atRiskLeads = leads?.filter(l => l?.intent_decay === 'fast')?.length;

  // DELIVERY CREW metrics
  const revenueInDelivery = clients?.reduce((sum, c) => sum + (parseFloat(c?.deal_value) || 0), 0);
  const activeClients = clients?.filter(c => c?.status === 'active' || c?.stage === 'active' || c?.stage === 'delivery')?.length;
  const pendingActions = clients?.filter(c => c?.next_action_status === 'pending_approval')?.length;
  const atRiskClients = clients?.filter(c => c?.flag === 'AT_RISK' || c?.flag === 'CRITICAL')?.length;

  const formatCurrency = (val) => {
    if (val >= 1000000) return `$${(val / 1000000)?.toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000)?.toFixed(0)}K`;
    return `$${val?.toFixed(0)}`;
  };

  return (
    <div style={{ minHeight: '100vh', background: BG, color: WHITE }}>
      {/* Top Bar */}
      <div style={{ borderBottom: `1px solid ${TEAL_BORDER}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(8,8,16,0.95)', position: 'sticky', top: 0, zIndex: 50 }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← RECALLCORE™
        </button>
        <div style={{ fontFamily: ORBITRON, fontSize: 14, fontWeight: 700, color: '#FFD700', letterSpacing: '0.1em' }}>OPERATIONS COMMAND</div>
        <div style={{ display: 'flex', gap: 16 }}>
          <button onClick={() => navigate('/lead-command-center')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: '0.1em' }}>LEADS</button>
          <button onClick={() => navigate('/delivery-command-center')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: '0.1em' }}>DELIVERY</button>
          <button onClick={() => navigate('/workspace')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: 11, color: MUTED, letterSpacing: '0.1em' }}>WORKSPACE</button>
        </div>
      </div>
      {/* Page Header */}
      <div style={{ padding: '32px 24px 0', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontFamily: ORBITRON, fontSize: '1.4rem', fontWeight: 700, color: WHITE, letterSpacing: '0.08em' }}>OPERATIONS COMMAND</span>
        </div>
        <div style={{ fontFamily: MONO, fontSize: '0.75rem', color: MUTED, letterSpacing: '0.1em', marginBottom: 32 }}>Sales Crew · Delivery Crew</div>
      </div>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 60px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, fontFamily: MONO, fontSize: '0.75rem', color: MUTED, letterSpacing: '0.1em' }}>
            LOADING OPERATIONS DATA...
          </div>
        ) : (
          <>
            {/* ─── PROOF OF WORK ─── */}
            <div style={{ marginBottom: 48 }}>
              <SectionHeader title="PROOF OF WORK" />
              {proofLoading ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {[...Array(6)]?.map((_, i) => (
                    <div key={i} style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px', opacity: 0.5 }}>
                      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 10 }}>LOADING...</div>
                      <div style={{ fontFamily: ORBITRON, fontSize: '1.4rem', fontWeight: 700, color: TEAL }}>—</div>
                    </div>
                  ))}
                </div>
              ) : proofError ? (
                <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED }}>
                  Unable to load proof of work data.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                  {[
                    { label: 'HOURS SAVED', key: 'hours_saved' },
                    { label: 'ACTIONS EXECUTED', key: 'actions_executed' },
                    { label: 'REVENUE INFLUENCED', key: 'revenue_influenced' },
                    { label: 'DELIVERABLES PRODUCED', key: 'deliverables_produced' },
                    { label: 'LEADS PROCESSED', key: 'leads_processed' },
                    { label: 'MISSIONS COMPLETED', key: 'missions_completed' },
                  ]?.map(({ label, key }) => (
                    <div key={key} style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: TEAL, opacity: 0.7, borderRadius: '6px 0 0 6px' }} />
                      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 10 }}>{label}</div>
                      <div style={{ fontFamily: ORBITRON, fontSize: '1.5rem', fontWeight: 700, color: TEAL }}>
                        {proofData?.[key] != null ? proofData?.[key] : 0}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ─── SECTION 1: SALES CREW ─── */}
            <div style={{ marginBottom: 48 }}>
              <SectionHeader title="SALES CREW" />
              {/* Metric Cards */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <MetricCard label="PIPELINE VALUE" value={formatCurrency(pipelineValue)} color={TEAL} />
                <MetricCard label="HOT LEADS" value={hotLeads} color={RED} />
                <MetricCard label="PENDING APPROVALS" value={pendingApprovals} color={ORANGE} />
                <MetricCard label="AT-RISK" value={atRiskLeads} color={RED} />
              </div>
              {/* Compact Lead List */}
              <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 1fr 80px 90px 40px 1fr', gap: 12, fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>
                  <span>NAME</span><span>COMPANY</span><span>VALUE</span><span>STAGE</span><span>INT</span><span>NEXT ACTION</span>
                </div>
                {leads?.slice(0, 6)?.map((lead, i) => (
                  <div key={lead?.id || i} style={{ padding: '10px 16px', borderBottom: i < Math.min(leads?.length, 6) - 1 ? `1px solid rgba(0,255,229,0.06)` : 'none', display: 'grid', gridTemplateColumns: '1fr 1fr 80px 90px 40px 1fr', gap: 12, alignItems: 'center' }}>
                    <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead?.name || '—'}</span>
                    <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead?.company || '—'}</span>
                    <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: GREEN }}>{lead?.deal_value ? formatCurrency(parseFloat(lead?.deal_value)) : '—'}</span>
                    <StagePill stage={lead?.stage} />
                    <IntentCircle score={lead?.intent_score} />
                    <span style={{ fontFamily: MONO, fontSize: '0.62rem', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead?.next_action || '—'}</span>
                  </div>
                ))}
                {leads?.length === 0 && (
                  <div style={{ padding: '20px 16px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED, textAlign: 'center' }}>No leads found.</div>
                )}
                <div style={{ padding: '10px 16px', borderTop: `1px solid ${TEAL_BORDER}`, display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => navigate('/lead-command-center')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: '0.65rem', color: TEAL, letterSpacing: '0.1em', textDecoration: 'underline' }}>VIEW ALL →</button>
                </div>
              </div>
            </div>

            {/* ─── SECTION 2: DELIVERY CREW ─── */}
            <div style={{ marginBottom: 48 }}>
              <SectionHeader title="DELIVERY CREW" />
              {/* Metric Cards */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <MetricCard label="REVENUE IN DELIVERY" value={formatCurrency(revenueInDelivery)} color={GREEN} />
                <MetricCard label="ACTIVE CLIENTS" value={activeClients} color={TEAL} />
                <MetricCard label="PENDING ACTIONS" value={pendingActions} color={ORANGE} />
                <MetricCard label="AT RISK" value={atRiskClients} color={RED} />
              </div>
              {/* Compact Client List */}
              <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 90px 80px 1fr', gap: 12, fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>
                  <span>NAME</span><span>COMPANY</span><span>PROJECT</span><span>STAGE</span><span>PROGRESS</span><span>NEXT ACTION</span>
                </div>
                {clients?.slice(0, 6)?.map((client, i) => {
                  const done = parseInt(client?.deliverables_done) || 0;
                  const total = parseInt(client?.deliverables_total) || 0;
                  return (
                    <div key={client?.id || i} style={{ padding: '10px 16px', borderBottom: i < Math.min(clients?.length, 6) - 1 ? `1px solid rgba(0,255,229,0.06)` : 'none', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 90px 80px 1fr', gap: 12, alignItems: 'center' }}>
                      <span style={{ fontFamily: MONO, fontSize: '0.7rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client?.name || '—'}</span>
                      <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client?.company || '—'}</span>
                      <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client?.project_name || '—'}</span>
                      <StagePill stage={client?.stage} />
                      <span style={{ fontFamily: MONO, fontSize: '0.65rem', color: total > 0 ? GREEN : MUTED }}>{total > 0 ? `${done}/${total}` : '—'}</span>
                      <span style={{ fontFamily: MONO, fontSize: '0.62rem', color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client?.next_action || '—'}</span>
                    </div>
                  );
                })}
                {clients?.length === 0 && (
                  <div style={{ padding: '20px 16px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED, textAlign: 'center' }}>No clients found.</div>
                )}
                <div style={{ padding: '10px 16px', borderTop: `1px solid ${TEAL_BORDER}`, display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => navigate('/delivery-command-center')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: '0.65rem', color: TEAL, letterSpacing: '0.1em', textDecoration: 'underline' }}>VIEW ALL →</button>
                </div>
              </div>
            </div>

            {/* ─── SECTION 3: CONTENT CREW ─── */}
            <div style={{ marginBottom: 48 }}>
              <SectionHeader title="CONTENT CREW" />
              {/* Mission Trigger Form */}
              <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '24px', marginBottom: 20 }}>
                <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: TEAL, letterSpacing: '0.14em', marginBottom: 20, paddingBottom: 12, borderBottom: `1px solid ${TEAL_BORDER}` }}>
                  MISSION TRIGGER
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {/* Goal */}
                  <div>
                    <label style={labelStyle}>GOAL</label>
                    <input
                      type="text"
                      value={contentForm?.goal}
                      onChange={e => setContentForm(prev => ({ ...prev, goal: e?.target?.value }))}
                      placeholder="e.g. Generate 30 days of content for product launch"
                      style={inputStyle}
                    />
                  </div>
                  {/* Audience */}
                  <div>
                    <label style={labelStyle}>AUDIENCE</label>
                    <input
                      type="text"
                      value={contentForm?.audience}
                      onChange={e => setContentForm(prev => ({ ...prev, audience: e?.target?.value }))}
                      placeholder="e.g. Small business owners aged 30-50"
                      style={inputStyle}
                    />
                  </div>
                  {/* Offer */}
                  <div>
                    <label style={labelStyle}>OFFER</label>
                    <input
                      type="text"
                      value={contentForm?.offer}
                      onChange={e => setContentForm(prev => ({ ...prev, offer: e?.target?.value }))}
                      placeholder="e.g. 90-day business acceleration program"
                      style={inputStyle}
                    />
                  </div>
                  {/* Tone */}
                  <div>
                    <label style={labelStyle}>TONE</label>
                    <select
                      value={contentForm?.tone}
                      onChange={e => setContentForm(prev => ({ ...prev, tone: e?.target?.value }))}
                      style={selectStyle}
                    >
                      {TONES?.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {/* Duration */}
                  <div>
                    <label style={labelStyle}>DURATION</label>
                    <select
                      value={contentForm?.duration}
                      onChange={e => setContentForm(prev => ({ ...prev, duration: e?.target?.value }))}
                      style={selectStyle}
                    >
                      {DURATIONS?.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                {/* Platform multi-select */}
                <div style={{ marginTop: 20 }}>
                  <label style={labelStyle}>PLATFORM</label>
                  <div style={multiSelectStyle(contentForm?.platform)}>
                    {PLATFORMS?.map(p => (
                      <span key={p} style={chipStyle(contentForm?.platform?.includes(p))} onClick={() => toggleMulti('platform', p)}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Content Types multi-select */}
                <div style={{ marginTop: 20 }}>
                  <label style={labelStyle}>CONTENT TYPES</label>
                  <div style={multiSelectStyle(contentForm?.content_types)}>
                    {CONTENT_TYPES?.map(ct => (
                      <span key={ct} style={chipStyle(contentForm?.content_types?.includes(ct))} onClick={() => toggleMulti('content_types', ct)}>
                        {ct}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Execute Button */}
                <div style={{ marginTop: 24 }}>
                  <style>{`@keyframes contentCrewSpin { to { transform: rotate(360deg); } }`}</style>
                  <button
                    onClick={executeContentMission}
                    disabled={contentRunning}
                    style={{
                      background: 'rgba(0,255,229,0.12)',
                      border: `1px solid ${TEAL}`,
                      borderRadius: 4, padding: '10px 28px',
                      fontFamily: ORBITRON, fontSize: '0.75rem', fontWeight: 700,
                      color: TEAL, letterSpacing: '0.15em',
                      cursor: contentRunning ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'inline-flex', alignItems: 'center',
                    }}
                  >
                    {contentRunning ? (
                      <>
                        <span style={spinnerStyle} />
                        EXECUTING MISSION...
                      </>
                    ) : '▶ EXECUTE CONTENT MISSION'}
                  </button>
                </div>
                {/* Error */}
                {contentError && (
                  <div style={{ marginTop: 14, fontFamily: MONO, fontSize: '0.68rem', color: RED, background: 'rgba(255,68,68,0.08)', border: `1px solid rgba(255,68,68,0.2)`, borderRadius: 4, padding: '8px 12px' }}>
                    ERROR: {contentError}
                  </div>
                )}
              </div>

              {/* Results Cards */}
              {contentResults?.length > 0 && (
                <div>
                  <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 14 }}>
                    MISSION OUTPUT — {contentResults?.length} DELIVERABLE{contentResults?.length !== 1 ? 'S' : ''}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
                    {contentResults?.map((card, idx) => {
                      const key = card?.id ?? idx;
                      const isApproved = approvedIds?.[key];
                      const isApproving = approvingIds?.[key];
                      const botName = card?.bot_name ?? card?.agent_name ?? card?.agent ?? card?.name ?? card?.role ?? card?.bot ?? card?.title ?? `BOT-${idx + 1}`;
                      const rawContent = card?.output ?? card?.deliverable ?? card?.content ?? card?.result ?? card?.text ?? JSON.stringify(card, null, 2);
                      return (
                        <div key={key} style={{ background: CARD_BG, border: `1px solid ${isApproved ? 'rgba(45,200,150,0.4)' : TEAL_BORDER}`, borderRadius: 6, padding: '18px', position: 'relative', overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: isApproved ? GREEN : TEAL, opacity: 0.7, borderRadius: '6px 0 0 6px' }} />
                          {/* Bot Name */}
                          <div style={{ fontFamily: ORBITRON, fontSize: '0.65rem', fontWeight: 700, color: isApproved ? GREEN : TEAL, letterSpacing: '0.12em', marginBottom: 10 }}>
                            {botName}
                          </div>
                          {/* Deliverable Output — rendered as formatted markdown */}
                          <div style={{ marginBottom: 14 }}>
                            <SimpleMarkdown content={rawContent} />
                          </div>
                          {/* Approve Button */}
                          <button
                            onClick={() => !isApproved && approveCard(card, idx)}
                            disabled={isApproved || isApproving}
                            style={{
                              background: isApproved ? 'rgba(45,200,150,0.12)' : 'rgba(0,255,229,0.08)',
                              border: `1px solid ${isApproved ? 'rgba(45,200,150,0.4)' : TEAL_BORDER}`,
                              borderRadius: 3, padding: '5px 16px',
                              fontFamily: MONO, fontSize: '0.6rem', fontWeight: 700,
                              color: isApproved ? GREEN : TEAL, letterSpacing: '0.1em',
                              cursor: isApproved ? 'default' : 'pointer',
                              transition: 'all 0.2s',
                            }}
                          >
                            {isApproved ? '✓ APPROVED' : isApproving ? 'APPROVING...' : 'APPROVE'}
                          </button>
                          {/* Download PDF Button */}
                          <button
                            onClick={() => {
                              const title = botName;
                              const content = rawContent;
                              const win = window.open('', '_blank');
                              if (!win) return;
                              win?.document?.write(`<!DOCTYPE html><html><head><title>${title}</title><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:0 24px;color:#111;line-height:1.7;font-size:15px;}h1{font-size:1.3rem;margin-bottom:24px;border-bottom:2px solid #333;padding-bottom:10px;}pre{white-space:pre-wrap;word-break:break-word;font-family:inherit;font-size:inherit;}@media print{body{margin:20px;}}</style></head><body><h1>${title}</h1><pre>${content?.replace(/</g,'&lt;')?.replace(/>/g,'&gt;')}</pre></body></html>`);
                              win?.document?.close();
                              win?.focus();
                              win?.print();
                            }}
                            style={{
                              background: 'rgba(245,158,11,0.15)',
                              border: '1px solid rgba(245,158,11,0.5)',
                              borderRadius: 3, padding: '5px 16px',
                              fontFamily: MONO, fontSize: '0.6rem', fontWeight: 700,
                              color: '#f59e0b', letterSpacing: '0.1em',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              marginLeft: 8,
                            }}
                          >
                            Download PDF
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* ─── SECTION 4: COMMAND BRIEFS ─── */}
            <div>
              <SectionHeader title="COMMAND BRIEFS" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Sales Command Brief */}
                <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px' }}>
                  <div style={{ fontFamily: MONO, fontSize: '0.65rem', color: TEAL, letterSpacing: '0.12em', marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${TEAL_BORDER}` }}>SALES BRIEF</div>
                  <SimpleMarkdown content={salesBrief?.content} />
                </div>
                {/* Delivery Command Brief */}
                <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px' }}>
                  <div style={{ fontFamily: MONO, fontSize: '0.65rem', color: TEAL, letterSpacing: '0.12em', marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${TEAL_BORDER}` }}>DELIVERY BRIEF</div>
                  <SimpleMarkdown content={deliveryBrief?.content} />
                </div>
              </div>
            </div>

            {/* ─── SECTION 5: TEAM WORKSPACE (ELITE ONLY) ─── */}
            {isElite && (
              <div style={{ marginTop: 48 }}>
                <SectionHeader title="TEAM WORKSPACE" />
                {workspaceLoading ? (
                  <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '32px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED, textAlign: 'center', letterSpacing: '0.1em' }}>
                    LOADING WORKSPACE...
                  </div>
                ) : workspaceError ? (
                  <div style={{ background: CARD_BG, border: `1px solid rgba(255,68,68,0.3)`, borderRadius: 6, padding: '20px', fontFamily: MONO, fontSize: '0.7rem', color: RED }}>
                    {workspaceError}
                  </div>
                ) : workspace ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Workspace Overview */}
                    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px' }}>
                      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: TEAL, letterSpacing: '0.14em', marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${TEAL_BORDER}` }}>
                        WORKSPACE OVERVIEW
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 160, background: 'rgba(0,0,0,0.3)', border: `1px solid ${TEAL_BORDER}`, borderRadius: 4, padding: '14px 16px' }}>
                          <div style={{ fontFamily: MONO, fontSize: '0.58rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 6 }}>WORKSPACE NAME</div>
                          <div style={{ fontFamily: ORBITRON, fontSize: '0.9rem', fontWeight: 700, color: WHITE }}>{workspace?.name || workspace?.workspace_name || '—'}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 140, background: 'rgba(0,0,0,0.3)', border: `1px solid ${TEAL_BORDER}`, borderRadius: 4, padding: '14px 16px' }}>
                          <div style={{ fontFamily: MONO, fontSize: '0.58rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 6 }}>SEATS USED</div>
                          <div style={{ fontFamily: ORBITRON, fontSize: '1.2rem', fontWeight: 700, color: TEAL }}>
                            {workspace?.seats_used ?? workspace?.members_count ?? '—'}<span style={{ fontSize: '0.7rem', color: MUTED }}> / 5</span>
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 160, background: 'rgba(0,0,0,0.3)', border: `1px solid ${TEAL_BORDER}`, borderRadius: 4, padding: '14px 16px' }}>
                          <div style={{ fontFamily: MONO, fontSize: '0.58rem', color: MUTED, letterSpacing: '0.12em', marginBottom: 6 }}>MISSIONS THIS MONTH</div>
                          <div style={{ fontFamily: ORBITRON, fontSize: '1.2rem', fontWeight: 700, color: GREEN }}>{workspace?.missions_this_month ?? workspace?.monthly_missions ?? '—'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Active Members */}
                    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, fontFamily: MONO, fontSize: '0.6rem', color: TEAL, letterSpacing: '0.14em' }}>
                        ACTIVE MEMBERS
                      </div>
                      {/* Header Row */}
                      <div style={{ padding: '8px 16px', borderBottom: `1px solid rgba(0,255,229,0.08)`, display: 'grid', gridTemplateColumns: '1fr 100px 80px', gap: 12, fontFamily: MONO, fontSize: '0.58rem', color: MUTED, letterSpacing: '0.1em' }}>
                        <span>EMAIL</span><span>MISSIONS</span><span></span>
                      </div>
                      {(workspace?.members || workspace?.active_members || [])?.length === 0 ? (
                        <div style={{ padding: '16px', fontFamily: MONO, fontSize: '0.68rem', color: MUTED, textAlign: 'center' }}>No active members.</div>
                      ) : (
                        (workspace?.members || workspace?.active_members || [])?.map((member, i) => {
                          const memberId = member?.id ?? member?.user_id ?? i;
                          const isRemoving = removingIds?.[memberId];
                          return (
                            <div key={memberId} style={{ padding: '10px 16px', borderBottom: `1px solid rgba(0,255,229,0.05)`, display: 'grid', gridTemplateColumns: '1fr 100px 80px', gap: 12, alignItems: 'center' }}>
                              <span style={{ fontFamily: MONO, fontSize: '0.68rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member?.email || '—'}</span>
                              <span style={{ fontFamily: MONO, fontSize: '0.68rem', color: TEAL_DIM }}>{member?.mission_count ?? member?.missions ?? '—'}</span>
                              <button
                                onClick={() => handleRemoveMember(memberId)}
                                disabled={isRemoving}
                                style={{
                                  background: 'rgba(255,68,68,0.08)',
                                  border: '1px solid rgba(255,68,68,0.3)',
                                  borderRadius: 3, padding: '3px 10px',
                                  fontFamily: MONO, fontSize: '0.58rem', fontWeight: 700,
                                  color: RED, letterSpacing: '0.08em',
                                  cursor: isRemoving ? 'not-allowed' : 'pointer',
                                  opacity: isRemoving ? 0.5 : 1,
                                  transition: 'all 0.15s',
                                }}
                                onMouseEnter={e => { if (!isRemoving) e.currentTarget.style.background = 'rgba(255,68,68,0.18)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,68,68,0.08)'; }}
                              >
                                {isRemoving ? '...' : 'REMOVE'}
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Pending Invites */}
                    {(workspace?.pending_invites || workspace?.invites || [])?.length > 0 && (
                      <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${TEAL_BORDER}`, fontFamily: MONO, fontSize: '0.6rem', color: ORANGE, letterSpacing: '0.14em' }}>
                          PENDING INVITES
                        </div>
                        {(workspace?.pending_invites || workspace?.invites || [])?.map((invite, i) => (
                          <div key={invite?.id ?? i} style={{ padding: '10px 16px', borderBottom: `1px solid rgba(0,255,229,0.05)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontFamily: MONO, fontSize: '0.68rem', color: MUTED }}>{invite?.email || '—'}</span>
                            <span style={{ fontFamily: MONO, fontSize: '0.58rem', color: ORANGE, letterSpacing: '0.08em', background: 'rgba(255,149,0,0.1)', border: '1px solid rgba(255,149,0,0.25)', borderRadius: 3, padding: '2px 8px' }}>PENDING</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Invite Form */}
                    <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px' }}>
                      <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: TEAL, letterSpacing: '0.14em', marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${TEAL_BORDER}` }}>
                        INVITE MEMBER
                      </div>
                      <form onSubmit={handleInvite} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e?.target?.value)}
                          placeholder="teammate@company.com"
                          required
                          style={{
                            flex: 1, minWidth: 200,
                            background: 'rgba(0,0,0,0.4)', border: `1px solid ${TEAL_BORDER}`,
                            borderRadius: 4, padding: '8px 12px',
                            fontFamily: MONO, fontSize: '0.72rem', color: WHITE,
                            outline: 'none', boxSizing: 'border-box',
                          }}
                          onFocus={e => { e.currentTarget.style.borderColor = TEAL; }}
                          onBlur={e => { e.currentTarget.style.borderColor = TEAL_BORDER; }}
                        />
                        <button
                          type="submit"
                          disabled={inviting || !inviteEmail?.trim()}
                          style={{
                            background: inviting ? TEAL_FAINT : 'rgba(0,255,229,0.12)',
                            border: `1px solid ${TEAL}`,
                            borderRadius: 4, padding: '8px 20px',
                            fontFamily: ORBITRON, fontSize: '0.68rem', fontWeight: 700,
                            color: TEAL, letterSpacing: '0.12em',
                            cursor: inviting || !inviteEmail?.trim() ? 'not-allowed' : 'pointer',
                            opacity: inviting || !inviteEmail?.trim() ? 0.6 : 1,
                            transition: 'all 0.15s', whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={e => { if (!inviting && inviteEmail?.trim()) e.currentTarget.style.background = 'rgba(0,255,229,0.2)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = inviting ? TEAL_FAINT : 'rgba(0,255,229,0.12)'; }}
                        >
                          {inviting ? 'INVITING...' : 'INVITE MEMBER'}
                        </button>
                      </form>
                      {inviteMsg && (
                        <div style={{
                          marginTop: 10, fontFamily: MONO, fontSize: '0.65rem',
                          color: inviteMsg?.type === 'success' ? GREEN : RED,
                          background: inviteMsg?.type === 'success' ? 'rgba(45,200,150,0.08)' : 'rgba(255,68,68,0.08)',
                          border: `1px solid ${inviteMsg?.type === 'success' ? 'rgba(45,200,150,0.25)' : 'rgba(255,68,68,0.25)'}`,
                          borderRadius: 4, padding: '6px 12px',
                        }}>
                          {inviteMsg?.text}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ background: CARD_BG, border: `1px solid ${TEAL_BORDER}`, borderRadius: 6, padding: '20px', fontFamily: MONO, fontSize: '0.7rem', color: MUTED, textAlign: 'center' }}>
                    No workspace data available.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Operations() {
  return (
    <RequireAuth>
      <OperationsPage />
    </RequireAuth>
  );
}
